import { useEffect, useState } from 'react';
import { Panel, PanelType, Stack } from '@fluentui/react';
import {
  Button,
  Dropdown,
  Field,
  FluentProvider,
  Option,
  webLightTheme,
} from '@fluentui/react-components';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { getLatestMetadatasNames } from '../../services/metadataService';
import { generateEmptyReportTemplate } from '../../services/reportTemplateService';

interface DownloadReportTemplatePanelProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const DownloadReportTemplatePanel = ({
  isOpen,
  onDismiss,
}: DownloadReportTemplatePanelProps) => {
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [metadataNames, setMetadataNames] = useState<string[]>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedMetadata('');
    const abortController = new AbortController();
    getLatestMetadatasNames(abortController, ['Work'])
      .then((names) =>
        setMetadataNames(names.map((n) => n.replace(/^Work\|/, '')))
      )
      .catch((err) =>
        setErrorMessage(err?.message || 'Failed to load metadata list.')
      );
    return () => abortController.abort();
  }, [isOpen, setErrorMessage]);

  const handleDownload = async () => {
    const abortController = new AbortController();
    try {
      setIsInProgress(true);
      const response = await generateEmptyReportTemplate(
        abortController,
        selectedMetadata
      );
      const blob = await response.blob();
      const fileName = `${selectedMetadata || 'EmptyReportTemplate'}.docx`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      onDismiss();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to download report template.');
    } finally {
      setIsInProgress(false);
    }
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close"
      headerText="Download template"
      headerTextProps={{ style: { fontSize: 20, fontWeight: 600 } }}
      onRenderHeader={(props, defaultRender) => <>{defaultRender?.(props)}</>}
    >
      <FluentProvider theme={webLightTheme}>
        <Stack tokens={{ childrenGap: 16 }}>
          <Field label="Select and download template" required>
            <Dropdown
              placeholder="Select a work form"
              value={selectedMetadata}
              selectedOptions={selectedMetadata ? [selectedMetadata] : []}
              onOptionSelect={(_event, data) =>
                setSelectedMetadata(data.optionValue ?? '')
              }
            >
              {metadataNames.map((name) => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            styles={{ root: { justifyContent: 'flex-end' } }}
          >
            <Button
              appearance="primary"
              disabled={!selectedMetadata}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Stack>
        </Stack>
      </FluentProvider>
    </Panel>
  );
};

export default DownloadReportTemplatePanel;
